<template>
  <div
    class="session"
    :class="{ 'session--whiteboard': auxiliaryType === 'WHITEBOARD' }"
  >
    <div
      v-if="sessionId"
      class="session-contents-container"
      v-bind:class="{
        'session-contents-container--mobile': mobileMode,
      }"
    >
      <div
        class="auxiliary-container"
        id="auxiliary-container"
        v-bind:class="{
          'auxiliary-container--hidden': shouldHideAuxiliarySection,
        }"
      >
        <whiteboard
          v-if="auxiliaryType === 'WHITEBOARD'"
          :sessionId="sessionId"
        />
        <document-editor v-else :sessionId="sessionId" />
      </div>

      <div
        class="chat-container"
        id="chat-container"
        v-bind:class="{
          'chat-container--hidden': shouldHideChatSection,
        }"
      >
        <div class="session-header-wrapper">
          <session-header @try-clicked="tryClicked" />
        </div>

        <div class="about-session-container" v-if="user.isVolunteer">
          <loading-message
            v-if="isLoadingPresessionResponse"
            :message="$t('session.loadingMessage')"
            class="about-session-loader"
          />
          <div
            v-else-if="studentPresessionResponses.length > 0"
            class="about-session-button"
            @click="handleAboutSessionClick"
          >
            {{ $t('session.aboutSessionButton') }}
            <caret-icon class="caret" />
          </div>
          <div
            v-else-if="
              showNoPresessionSurveyResponse ||
                studentPresessionResponses.length === 0
            "
            class="about-session-no-responses"
          >
            {{ $t('session.aboutSessionNoResponses') }}
          </div>
          <question-mark-icon
            v-if="mobileMode"
            @click="openHelp"
            class="help-icon"
          />
        </div>
        <div
          class="about-session-container"
          v-else-if="mobileMode && !user.isVolunteer"
        >
          <question-mark-icon @click="openHelp" class="help-icon" />
        </div>

        <session-chat
          :currentSession="session"
          :shouldHideChatSection="shouldHideChatSection"
          :setHasSeenNewMessage="setHasSeenNewMessage"
          :isSessionConnectionAlive="isSessionConnectionAlive"
          :isSessionAlive="isSessionAlive"
          :sessionHasEnded="sessionHasEnded"
        />

        <div class="chat-input-container" v-if="!mobileMode">
          <voice-call
            v-if="sessionId"
            :sessionId="sessionId"
            :currentUserId="user._id"
            @joined="handleVoiceJoined"
            @ended="handleVoiceEnded"
            @error="handleVoiceError"
            @autoplay-blocked="handleAutoplayBlocked"
          />
        </div>
      </div>
    </div>
    <div v-if="mobileMode && sessionId" class="mobile-footer">
      <div class="voice-call-wrapper">
        <voice-call
          :sessionId="sessionId"
          :currentUserId="user._id"
          @joined="handleVoiceJoined"
          @ended="handleVoiceEnded"
          @error="handleVoiceError"
          @autoplay-blocked="handleAutoplayBlocked"
        />
      </div>

      <div class="toggleButton" id="toggleButton" @click="toggleAuxiliary">
        <div class="toggleButton__wrapper">
          <span
            class="toggleButton__message-indicator"
            v-if="!hasSeenNewMessage"
          ></span>
          <img id="toggleIcon" :src="toggleIconSrc" alt="toggle icon" />
        </div>
      </div>
    </div>

    <div
      v-if="showPhotoUpload"
      class="toggleButton toggleButton__photo-upload"
      :class="shouldHideChatSection ? 'photo-upload--hidden' : ''"
      @click="openFileDialog"
    >
      <photo-upload-icon class="photo-upload--icon" />
    </div>

    <web-notifications-modal
      v-if="showNotificationModal"
      :closeModal="() => setShowNotificationModal(false)"
    />

    <about-session-modal
      v-if="showAboutSessionModal"
      :closeModal="toggleAboutSessionModal"
      :responses="studentPresessionResponses"
      :totalStudentSessions="totalStudentSessions"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import NetworkService from '@/services/NetworkService'
import SessionService from '@/services/SessionService'
import AnalyticsService from '@/services/AnalyticsService'
import SessionHeader from './SessionHeader'
import SessionChat from './SessionChat'
import Whiteboard from './Whiteboard'
import DocumentEditor from './DocumentEditor'
import VoiceCall from './VoiceCall'
import SessionFulfilledModal from './SessionFulfilledModal'
import ConnectionTroubleModal from './ConnectionTroubleModal'
import PhotoUploadIcon from '@/assets/whiteboard_icons/photo-upload.svg'
import isOutdatedMobileAppVersion from '@/utils/is-outdated-mobile-app-version'
import CaretIcon from '@/assets/caret.svg'
import QuestionMarkIcon from '@/assets/question-mark-icon.svg'
import WebNotificationsModal from '@/components/WebNotificationsModal.vue'
import AboutSessionModal from './AboutSessionModal'
import getNotificationPermission from '@/utils/get-notification-permission'
import { EVENTS, SESSION_TOOL_TYPES } from '@/consts'
import { backOff } from 'exponential-backoff'
import LoadingMessage from '@/components/LoadingMessage.vue'
import LoggerService from '@/services/LoggerService'
import i18n from '@/i18n'

const activeHeaderData = {
  component: 'SessionHeader',
}
const COUNSELING_TOPICS = ['college', 'counseling']
export default {
  name: 'session-view',
  components: {
    SessionHeader,
    SessionChat,
    Whiteboard,
    PhotoUploadIcon,
    DocumentEditor,
    VoiceCall,
    WebNotificationsModal,
    CaretIcon,
    AboutSessionModal,
    LoadingMessage,
    QuestionMarkIcon,
  },
  created() {
    if (this.mobileMode) {
      this.$store.dispatch('app/hideNavigation')
    } else {
      this.$store.dispatch('app/header/show', activeHeaderData)
      this.$store.dispatch('app/sidebar/hide')
    }

    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  /*
   * @notes
   * [1] Refactoring candidate: it'd be awesome if Dashboard could pass
   *     the topic directly
   */
  data() {
    return {
      showMedia: false,
      auxiliaryOpen: false,
      sessionId: null,
      hasSeenNewMessage: true,
      showNotificationModal: false,
      showAboutSessionModal: false,
      studentPresessionResponses: [],
      totalStudentSessions: 0,
      showNoPresessionSurveyResponse: false,
      isLoadingPresessionResponse: false,
      isFetchingIsSessionRecapEligible: false,
      sessionHasEnded: false,
      voiceCallActive: false,
    }
  },
  // beforeRouteEnter(to, from, next) {
  //   next(vm => {
  //     vm.prevRoute = from
  //   })
  // },
  computed: {
    ...mapState({
      user: state => state.user.user,
      session: state => state.user.session,
      isSessionConnectionAlive: state => state.user.isSessionConnectionAlive,
      isMobileApp: state => state.app.isMobileApp,
      presessionSurvey: state => state.user.presessionSurvey,
    }),
    ...mapGetters({
      mobileMode: 'app/mobileMode',
      isAuthenticated: 'user/isAuthenticated',
      isVolunteer: 'user/isVolunteer',
      isSessionOver: 'user/isSessionOver',
      isSessionAlive: 'user/isSessionAlive',
      isSessionRecapDmsActive: 'featureFlags/isSessionRecapDmsActive',
      isRecapSocketUpdatesActive: 'featureFlags/isRecapSocketUpdatesActive',
    }),

    auxiliaryType() {
      const sessionTopic = (
        this.session.type ||
        this.$route.params.topic ||
        ''
      ).toLowerCase()

      if (COUNSELING_TOPICS.includes(sessionTopic)) return 'WHITEBOARD'

      if (this.session.toolType === SESSION_TOOL_TYPES.DOCUMENT_EDITOR)
        return 'DOCUMENT'
      else return 'WHITEBOARD'
    },

    toggleIconSrc() {
      if (this.auxiliaryOpen) return require(`@/assets/Chat.png`)
      else if (this.auxiliaryType === 'WHITEBOARD')
        return require(`@/assets/Pencil.png`)
      else return require(`@/assets/doc_editor_icon.png`)
    },

    shouldHideAuxiliarySection() {
      // Never hide auxiliary section (whiteboard/document) on desktop
      if (!this.mobileMode) {
        return false
      }

      return !this.auxiliaryOpen
    },
    shouldHideChatSection() {
      // Never hide chat section on desktop
      if (!this.mobileMode) {
        return false
      }

      return this.auxiliaryOpen
    },
    showPhotoUpload() {
      if (this.auxiliaryType !== 'WHITEBOARD') return false
      if (this.mobileMode) return false
      if (!this.isVolunteer && this.mobileMode) {
        if (this.isMobileApp && isOutdatedMobileAppVersion()) return false
        return true
      }

      return false
    },
  },
  async mounted() {
    const {
      data: { isValid },
    } = await NetworkService.getIsSubjectValid(
      this.$route.params.subTopic,
      this.$route.params.topic
    )
    if (!isValid) return this.$router.push(`/${i18n.locale}/dashboard`)

    const id = this.$route.params.sessionId

    let promise

    if (!id) {
      let type = this.$route.params.topic
      promise = SessionService.newSession(
        this,
        type,
        this.$route.params.subTopic,
        {
          onRetry: (res, abort) => {
            this.showTroubleStartingModal(abort)
          },
        }
      )
    } else {
      promise = SessionService.useExistingSession(this, id, {
        onRetry: (res, abort) => {
          this.showTroubleJoiningModal(abort)
        },
      })
    }

    promise
      .then(async sessionId => {
        this.sessionId = sessionId
        if (!id && !this.isVolunteer)
          AnalyticsService.captureEvent(EVENTS.SESSION_REQUESTED, {
            event: EVENTS.SESSION_REQUESTED,
            sessionId,
            subject: this.$route.params.subTopic,
          })

        // If we have a pre-session survey, submit it now
        if (Object.keys(this.presessionSurvey).length) {
          try {
            await backOff(() =>
              NetworkService.submitSurvey(
                Object.assign({}, this.presessionSurvey, { sessionId })
              )
            )
          } catch (err) {
            LoggerService.noticeError(err)
          }
          this.$store.dispatch('user/clearPresessionSurvey')
        }

        // ensure we restore user when we get a successful response
        if (!this.isAuthenticated) {
          this.$store.dispatch('user/fetchUser')
        }

        if (!this.$socket.connected && !this.isRecapSocketUpdatesActive)
          await this.$socket.connect()
        this.joinSession(sessionId)
        this.$store.dispatch('user/sessionConnected')

        if (this.user.isVolunteer) {
          await this.getSessionContext(sessionId)
        }

        if (
          (this.user.isVolunteer &&
            (!this.user.isOnboarded || !this.user.isApproved)) ||
          this.isMobileApp
        )
          this.showNotificationModal = false

        if (getNotificationPermission() === 'default')
          this.showNotificationModal = true
      })
      .catch(err => {
        if (err?.response?.status !== 0 && err.code !== 'EUSERABORTED') {
          window.alert('Could not start new help session')
          LoggerService.noticeError(err)
        }
        this.$router.replace(`/${i18n.locale}/dashboard`)
      })
  },
  sockets: {
    bump: function(data) {
      this.$store.dispatch('app/modal/show', {
        component: SessionFulfilledModal,
        data: {
          acceptText: this.$root.$t('session.socketAcceptText'),
          alertModal: true,
          isSessionEnded: !!data.endedAt,
          volunteerJoined: !!data.volunteer,
          isSessionVolunteer: this.user._id === data.volunteer,
          isSessionStudent: this.user._id === data.student,
        },
      })
    },
    reconnect_attempt() {
      this.$store.dispatch('user/sessionDisconnected')
      if (!this.session || !this.session._id) {
        const abort = () => this.$router.push(`/${i18n.locale}/dashboard`)
        this.showTroubleStartingModal(abort)
      }
    },
    // https://socket.io/docs/v2/client-api/#event-disconnect
    async disconnect(reason) {
      const userType = this.isVolunteer
        ? this.$root.$t('session.volunteer')
        : this.$root.$t('session.student')

      const err = new Error(
        this.$root.$t('session.errorMessage', {
          userType: userType,
          sessionId: this.sessionId,
          reason: reason,
        })
      )

      LoggerService.noticeError(err)

      if (
        reason === 'io server disconnect' &&
        !this.isRecapSocketUpdatesActive
      ) {
        // the disconnection was initiated by the server, you need to reconnect manually
        if (!this.$socket.connected) await this.$socket.connect()
        this.joinSession(this.sessionId)
        this.$store.dispatch('user/sessionConnected')
      }
    },
    connect() {
      if (this.isRecapSocketUpdatesActive) this.joinSession(this.sessionId)
      this.$store.dispatch('user/sessionConnected')
    },
  },
  methods: {
    handleResize() {
      if (this.mobileMode) {
        this.$store.dispatch('app/hideNavigation')
      } else {
        this.$store.dispatch('app/header/show', activeHeaderData)
        this.$store.dispatch('app/sidebar/hide')
      }
    },
    toggleAuxiliary() {
      if (!this.auxiliaryOpen) {
        document.getElementById('toggleButton').classList.add('back')
        this.auxiliaryOpen = true
      } else {
        document.getElementById('toggleButton').classList.remove('back')
        this.auxiliaryOpen = false
      }

      if (this.shouldHideAuxiliarySection) this.hasSeenNewMessage = true
    },
    async joinSession(sessionId) {
      // await nextTick to get access to this.prevRoute and avoid a race condition
      await this.$nextTick()
      this.$socket.emit(
        'join',
        {
          sessionId,
          user: this.user,
          // helps track where volunteers are joining a session from
          // if a volunteer joins using a URL from a text notification, resolve to an empty string
          joinedFrom:
            this.prevRoute && this.prevRoute.name ? this.prevRoute.name : '',
        },
        1
      )
    },
    showTroubleStartingModal(abort) {
      const TROUBLE_STARTING_MESSAGE = this.$root.$t(
        'session.troubleStartingMessage'
      )

      this.showConnectionTroubleModal(abort, TROUBLE_STARTING_MESSAGE)
    },
    showTroubleJoiningModal(abort) {
      const TROUBLE_JOINING_MESSAGE = this.$root.$t(
        'session.troubleJoiningMessage'
      )

      this.showConnectionTroubleModal(abort, TROUBLE_JOINING_MESSAGE)
    },
    showConnectionTroubleModal(abort, message) {
      this.$store.dispatch('app/modal/show', {
        component: ConnectionTroubleModal,
        data: {
          message,
          acceptText: this.$root.$t('session.ConnectionTroubleModalAcceptText'),
          alertModal: true,
          abortFunction: abort,
        },
      })
    },
    tryClicked() {
      this.sessionReconnecting = true
    },
    openFileDialog(event) {
      this.$refs.whiteboard.openFileDialog(event)
    },
    setHasSeenNewMessage(value) {
      this.hasSeenNewMessage = value
    },
    setShowNotificationModal(value) {
      this.showNotificationModal = value
    },
    handleAboutSessionClick() {
      AnalyticsService.captureEvent(EVENTS.VOLUNTEER_CLICKED_ABOUT_SESSION)
      this.toggleAboutSessionModal()
    },
    toggleAboutSessionModal() {
      this.showAboutSessionModal = !this.showAboutSessionModal
    },
    async getSessionContext(sessionId) {
      try {
        this.isLoadingPresessionResponse = true
        const presessionSurveyResponse = await NetworkService.getPresessionSurveyResponse(
          sessionId
        )
        this.totalStudentSessions =
          presessionSurveyResponse.data.totalStudentSessions
        this.studentPresessionResponses =
          presessionSurveyResponse.data.responses
      } catch (err) {
        this.showNoPresessionSurveyResponse = true
      } finally {
        this.isLoadingPresessionResponse = false
      }
    },
    openHelp() {
      AnalyticsService.captureEvent(EVENTS.USER_CLICKED_IN_SESSION_HELP)
    },
    // Voice call handlers
    handleVoiceJoined() {
      console.log('Voice call joined')
      this.voiceCallActive = true
      AnalyticsService.captureEvent(EVENTS.VOICE_CALL_STARTED, {
        sessionId: this.sessionId,
      })
    },
    handleVoiceEnded() {
      console.log('Voice call ended')
      this.voiceCallActive = false
      AnalyticsService.captureEvent(EVENTS.VOICE_CALL_ENDED, {
        sessionId: this.sessionId,
      })
    },
    handleVoiceError(error) {
      console.error('Voice call error:', error)
      this.$store.dispatch('app/modal/show', {
        component: 'ErrorModal',
        data: {
          message: error.message,
          alertModal: true,
        },
      })
    },
    handleAutoplayBlocked() {
      // Show a message to the user
      this.$store.dispatch('app/modal/show', {
        component: 'InfoModal',
        data: {
          message: 'Please click OK to enable audio from other participants',
          acceptText: 'OK',
          alertModal: true,
        },
      })
    },
  },
  watch: {
    isSessionConnectionAlive(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.$store.dispatch('app/modal/hide')
      }
    },
    // Once session has ended, show chatbot to tutor
    async 'session.endedAt'(newValue, oldValue) {
      if (newValue && !oldValue) this.sessionHasEnded = true
    },
  },
}
</script>

<style lang="scss" scoped>
.session {
  position: relative;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  &--whiteboard {
    .toggleButton.back {
      bottom: calc(100% - 140px);
      right: 35px;
      @include breakpoint-below('small') {
        right: 20px;
        bottom: 100px;
      }
    }
  }
}

.mobile-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  box-sizing: border-box;
  gap: 12px;
  background: rgba(240, 244, 248, 0.96);
  backdrop-filter: blur(10px);
  pointer-events: none;
  // این خط از دریافت کلیک توسط کانتینر جلوگیری می‌کند و فقط دکمه‌ها قابل کلیک خواهند بود

  @supports (padding: max(0px)) {
    padding-bottom: max(12px, calc(12px + env(safe-area-inset-bottom)));
  }
  @include breakpoint-above('medium') {
    display: none;
  }
}
.voice-call-wrapper {
  flex: 1;
  display: flex;
  pointer-events: auto;
  background: #f0f4f8;
  border-radius: 25px;
  padding: 6px 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.session-contents-container--mobile {
  padding-bottom: 72px;
  padding-bottom: calc(72px + env(safe-area-inset-bottom));
}

.session-main-header {
  position: relative;
  z-index: 3;
  width: 100%;
  background: #fff;
  height: 70px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.session-contents-container {
  flex-grow: 1;
  display: flex;
  background: $c-background-grey;
  align-items: stretch;
  overflow: hidden;
  @include breakpoint-above('medium') {
    padding: 20px;
    @include child-spacing(right, 15px);
  }
}

.about-session {
  &-container {
    background-color: $light-blue-background;
    z-index: 1;
    padding: 0.75em 0.6em;
    width: 100%;
    @include flex-container(row, space-between, center);
  }

  &-button {
    @include font-category('subheading');
    background-color: $light-blue-background;

    &:hover {
      background-color: rgba(196, 196, 196, 0.2);
      cursor: pointer;
    }

    border-radius: 4px;
    padding: 0.4rem 0.5rem;
  }

  &-no-responses {
    @include font-category('subheading');
    padding: 0.4rem 0.5rem;
  }

  &-loader {
    @include font-category('subheading');
    padding: 0.4rem 0.5rem;
  }
}

.auxiliary-container,
.chat-container {
  height: calc(100% - 70px);
  @include breakpoint-above('medium') {
    border-radius: 8px;
  }

  @include breakpoint-below('medium') {
    width: 100%;
  }
}

.auxiliary-container {
  background: #fff;
  padding: 0;
  flex-grow: 1;
  position: relative;

  &--hidden {
    position: absolute;
    width: 100%;
    height: 100%;
    top: -500px;
    left: -500px;
  }
}

.chat-container {
  padding: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;

  &--hidden {
    display: none;
  }

  @include breakpoint-above('medium') {
    min-width: 300px;
    flex-basis: 300px;
    position: relative;
  }

  @include breakpoint-above('large') {
    min-width: 400px;
    flex-basis: 400px;
  }
}

.chat-input-container {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background-color: #fff;
  border-top: 1px solid $c-border-grey;
  border-radius: 0 0 8px 8px;
  margin-top: auto;
  min-height: 60px;
  @include breakpoint-below('medium') {
    display: none;
  }
}

// .voice-call-mobile {
//   position: fixed;
//   bottom: 20px;
//   left: 20px;
//   z-index: 3;
//   background: #f0f4f8;
//   border-radius: 25px;
//   padding: 5px;
//   box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
//   max-width: calc(100vw - 120px); /* فضا برای دکمه toggle */
// }

.toggleButton {
  // حذف position, bottom, right, z-index
  border-radius: 20px;
  background-color: #16d2aa;
  width: 40px;
  height: 40px;
  transition: 0.4s;
  text-align: center;
  flex-shrink: 0; // جلوگیری از کوچک شدن دکمه در فضاهای کم
  pointer-events: auto; // این دکمه را قابل کلیک می‌کند

  // حذف @include breakpoint-below('medium') چون دیگر نیازی به آن نیست

  img,
  svg {
    margin-top: 7px;
    width: 26px;
    height: 26px;
  }
}
.toggleButton__photo-upload {
  position: fixed;
  z-index: 3;
  bottom: 10px;
  right: 20px; /* موقعیت پیش‌فرض برای دسکتاپ */
  border-radius: 20px;
  background-color: #16d2aa;
  width: 40px;
  height: 40px;
  transition: 0.4s;
  text-align: center;

  @include breakpoint-below('medium') {
    bottom: 20px;
    right: 20px; /* تغییر: از left به right */
    left: unset; /* حذف left */
  }

  &__photo-upload {
    background-color: white;
    border: 1px solid $c-border-grey;
    right: 80px;
  }

  img,
  svg {
    margin-top: 7px;
    width: 26px;
    height: 26px;
  }
}

.toggleButton__wrapper {
  position: relative;
}

.toggleButton__message-indicator {
  position: absolute;
  display: inline-block;
  width: 10px;
  height: 10px;
  background-color: $c-error-red;
  top: 0;
  right: 0;
  border-radius: 50%;
}

.photo-upload--hidden {
  display: none !important;
}

.photo-upload--icon {
  margin-top: 5px !important;
}

.caret {
  fill: #000;
}

.help-icon {
  width: 30px;
  height: 30px;
  margin-left: auto;
  fill: $c-information-blue;
  transition: scale 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    transform: scale(1.1);
  }
}

html.is-rtl {
  .session {
    direction: ltr;
  }

  .auxiliary-container {
    direction: rtl;
  }
  .session-main-header {
    direction: rtl;
  }
}
.session-header-wrapper {
  width: 100%;
  background: #fff;
  position: relative;
  z-index: 3;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 60px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
</style>
